import type { TBrand, TLocale } from 'types/common/common.types'

/**
 * Map path locale prefix -> expected Country Code (ISO-2, lowercase)
 */
export const LOCALE_COUNTRY_MAP: Record<TLocale, string> = {
  ar: 'ar',
  uy: 'uy',
  co: 'co',
  cl: 'cl',
  ec: 'ec',
  pe: 'pe',
  mx: 'mx',
  br: 'br'
}

/**
 * Countries served by each site/brand.
 * - 'dgo' (market 'ssla'): all Spanish-speaking locales
 * - 'sky-mais' (market 'br'): Brazil
 */
export const SITE_COUNTRIES: Record<TBrand, string[]> = {
  dgo: ['ar', 'uy', 'co', 'cl', 'ec', 'pe', 'mx'],
  'sky-mais': ['br']
}

export interface GeoScenario {
  /** CloudFront-detected country, normalized lowercase (null = unknown) */
  userCountry: string | null
  /** Locale of the landed page (from LocaleContext) */
  currentLocale: TLocale | null
  /** Site the visitor landed on */
  currentSite: TBrand
  /** Site matching the visitor's country (null = unsupported country) */
  visitorSite: TBrand | null
  /** Landed locale's expected country differs from user country (same-site case) */
  isMismatch: boolean
  /** Visitor's country belongs to the OTHER site (cross-site case) */
  isCrossSite: boolean
  /** Visitor's country is not served by either site (e.g. VE, US) */
  isUnsupportedCountry: boolean
  /** Locale path prefix matching user's country within the current site (same-site CTA) */
  suggestedLocale: TLocale | null
  /** Whether the modal should be shown at all */
  shouldShowModal: boolean
}

/**
 * Resolves the geo scenario from the detected country, the landed locale and the
 * current site (brand). Pure function so it can be unit-tested for all corner cases.
 *
 * Corner cases handled:
 * - Unknown country (no header yet / fetch failed) -> no modal
 * - Unsupported country (e.g. VE, US) -> modal allowed (pending UX), no redirect target
 * - Cross-site visitor (BR user in DGO, SSLA user in SKY+) -> modal, CTA goes to other site
 * - Same-site locale mismatch (e.g. EC user on /ar) -> modal, CTA to suggested locale
 */
export function resolveGeoScenario(
  userCountry: string | null | undefined,
  currentLocale: TLocale | null | undefined,
  currentSite: TBrand
): GeoScenario {
  const normalizedCountry = userCountry?.toLowerCase() || null
  const siteCountries = SITE_COUNTRIES[currentSite] || []
  const isVisitorOnOwnSite = Boolean(
    normalizedCountry && siteCountries.includes(normalizedCountry)
  )

  const visitorSite =
    (Object.keys(SITE_COUNTRIES) as TBrand[]).find((brand) =>
      SITE_COUNTRIES[brand].includes(normalizedCountry ?? '')
    ) || null

  const expectedCountry = currentLocale
    ? LOCALE_COUNTRY_MAP[currentLocale]
    : undefined

  // Same-site mismatch: visitor is on their own site but the wrong locale
  const isMismatch = Boolean(
    isVisitorOnOwnSite &&
      expectedCountry &&
      normalizedCountry !== expectedCountry
  )

  // Cross-site: visitor's country belongs to the other site entirely
  const isCrossSite =
    Boolean(normalizedCountry) && !isVisitorOnOwnSite && visitorSite !== null

  // Unsupported country: detected but not served by either site (e.g. VE, US)
  const isUnsupportedCountry =
    Boolean(normalizedCountry) && !isVisitorOnOwnSite && visitorSite === null

  // Same-site redirect target: locale whose country matches the user's country
  const suggestedLocale =
    (Object.keys(LOCALE_COUNTRY_MAP) as TLocale[]).find(
      (key) => LOCALE_COUNTRY_MAP[key] === normalizedCountry
    ) || null

  // Modal shows for: same-site locale mismatch OR cross-site visitor.
  // Unsupported countries: we cannot block these users — QA/UX decision.
  // Only logged (see GeoProvider); a country-picker modal will come in a
  // separate feature.
  const shouldShowModal = isMismatch || isCrossSite

  return {
    userCountry: normalizedCountry,
    currentLocale: currentLocale ?? null,
    currentSite,
    visitorSite,
    isMismatch,
    isCrossSite,
    isUnsupportedCountry,
    suggestedLocale,
    shouldShowModal
  }
}
